import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { Button } from '@/components/ui/button'
import Modal from '@/components/ui/modal'
import { Input } from '@/components/ui/input'
import { contactsApi, type Contact } from '@/services/contactsApi'
import { botSettingsApi } from '@/services/botSettingsApi'
import { conversationsApi } from '@/services/conversationsApi'
import { listChannelInstances, type ChannelInstanceResponse } from '@/services/channelsApi'
import { getProducts, type Product } from '@/lib/api/products'
import { ordersApi, type OrderSource } from '@/services/ordersApi'
import { OrderWizardStepper, OrderWizardSummaryBar } from './OrderWizardChrome'
import {
  buildOrderValidationMessage,
  formatOrderMoney,
  getOrderStatusBadge,
  getFulfillmentModeLabel,
  isDeliveryLikeMethod,
  isPickupLikeMethod,
  isWhatsAppChannelType,
  matchProductByNameOrSku,
  normalizeProductSearchText,
  resolveOrderChannelInstanceId,
} from './ordersUx'
import {
  CheckCircle2,
  Clock3,
  Home,
  Loader2,
  Package,
  Plus,
  Search,
  ShoppingCart,
  Bot,
  Truck,
  User,
  Trash2,
} from 'lucide-react'
import { useVerticalUI } from '@/lib/verticalUI'
import { queryKeys } from '@/lib/queryKeys'
import { useAuthStore } from '@/store/authStore'

type OrderFormItem = { productId: string; quantity: number }

interface FormState {
  source: OrderSource
  contactId: string
  conversationId: string
  channelInstanceId: string
  customerName: string
  customerPhone: string
  customerEmail: string
  fulfillmentMethod: string
  fulfillmentAddress: string
  fulfillmentReference: string
  notes: string
  items: OrderFormItem[]
}

const WIZARD_STEPS = [
  { step: 1, title: 'Cliente', subtitle: 'Busca o crea un cliente' },
  { step: 2, title: 'Productos', subtitle: 'Arma la orden' },
  { step: 3, title: 'Detalles', subtitle: 'Entrega y confirmación' },
] as const

const ORDER_NOTE_TEMPLATES = [
  'Entregar en horario comercial.',
  'Confirmar stock antes del despacho.',
  'Cliente solicita aviso previo.',
  'Retiro en tienda con nombre del titular.',
]

function createEmptyOrderItem(productId = '', quantity = 1): OrderFormItem {
  return { productId, quantity: Math.max(1, Number(quantity || 1)) }
}

function createDefaultForm(overrides: Partial<FormState> = {}): FormState {
  return {
    source: overrides.source ?? 'manual',
    contactId: overrides.contactId ?? '',
    conversationId: overrides.conversationId ?? '',
    channelInstanceId: overrides.channelInstanceId ?? '',
    customerName: overrides.customerName ?? '',
    customerPhone: overrides.customerPhone ?? '',
    customerEmail: overrides.customerEmail ?? '',
    fulfillmentMethod: overrides.fulfillmentMethod ?? '',
    fulfillmentAddress: overrides.fulfillmentAddress ?? '',
    fulfillmentReference: overrides.fulfillmentReference ?? '',
    notes: overrides.notes ?? '',
    items: overrides.items?.length ? overrides.items.map((item) => createEmptyOrderItem(item.productId, item.quantity)) : [],
  }
}

function isOrderSource(value: string | null): value is OrderSource {
  return value === 'manual' || value === 'catalog' || value === 'checkout'
}

interface OrderCreateWizardProps {
  onClose: () => void
}

export default function OrderCreateWizard({ onClose }: OrderCreateWizardProps) {
  const { profile } = useVerticalUI()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const tenantId = useAuthStore((state) => state.tenantId)
  const [searchParams] = useSearchParams()
  const searchParamsKey = searchParams.toString()
  const seedConversationId = searchParams.get('conversationId') ?? ''

  const [form, setForm] = useState<FormState>(() => createDefaultForm())
  const [wizardStep, setWizardStep] = useState<1 | 2 | 3>(1)
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null)
  const [contactSearch, setContactSearch] = useState('')
  const [contactSearchDebounced, setContactSearchDebounced] = useState('')
  const [quickContactOpen, setQuickContactOpen] = useState(false)
  const [quickContact, setQuickContact] = useState({ waContact: '', name: '', phone: '', email: '' })
  const [productSearch, setProductSearch] = useState('')

  const channelInstancesQuery = useQuery({
    queryKey: queryKeys.orderChannelInstances(tenantId),
    queryFn: () => listChannelInstances(),
    staleTime: 60_000,
  })

  const botSettingsQuery = useQuery({
    queryKey: queryKeys.orderBotSettings(tenantId),
    queryFn: () => botSettingsApi.getSettings(),
    staleTime: 60_000,
  })

  const contactsQuery = useQuery({
    queryKey: queryKeys.orderContacts(tenantId, contactSearchDebounced),
    queryFn: () => contactsApi.getContacts({ search: contactSearchDebounced, limit: 8, page: 1 }),
    staleTime: 15_000,
  })

  const conversationQuery = useQuery({
    queryKey: queryKeys.orderConversation(tenantId, seedConversationId),
    queryFn: () => conversationsApi.getConversationById(seedConversationId),
    enabled: Boolean(seedConversationId),
  })

  const recentOrdersQuery = useQuery({
    queryKey: ['orders', tenantId ?? 'no-tenant', 'create-recent', selectedContact?.id],
    queryFn: () => ordersApi.getOrders({ contactId: selectedContact!.id, page: 1, limit: 5 }),
    enabled: Boolean(selectedContact?.id),
    staleTime: 20_000,
  })

  const productsQuery = useQuery({
    queryKey: queryKeys.orderProducts(tenantId),
    queryFn: () => getProducts(),
  })

  const availableProducts = useMemo(() => productsQuery.data ?? [], [productsQuery.data])

  const conversationSuggestions = useMemo(() => {
    const conv = conversationQuery.data
    const salesCtx = conv?.salesContext
    const activeDraft = salesCtx?.activeOrderDraft
    const handoff = salesCtx?.handoff
    const products = availableProducts

    const list: Array<{ name: string; quantity: number; price?: number; matchedProduct: Product | null }> = []

    const pushSuggestion = (title: string, quantity: number, price?: number, productId?: string | null) => {
      let matched: Product | null = productId ? products.find((product) => product.id === productId) ?? null : null
      if (!matched) {
        const matchedId = matchProductByNameOrSku(title, products)
        matched = matchedId ? products.find((product) => product.id === matchedId) ?? null : null
      }
      list.push({ name: title, quantity: Math.max(1, Number(quantity || 1)), price, matchedProduct: matched })
    }

    if (activeDraft?.items?.length) {
      activeDraft.items.forEach((item) => {
        pushSuggestion(
          item.title || 'Producto acordado',
          Number(item.quantity || 1),
          item.unitPriceCents ? item.unitPriceCents / 100 : undefined,
          item.productId
        )
      })
    } else if (handoff?.items?.length) {
      handoff.items.forEach((item) => pushSuggestion(item.name, Number(item.quantity || 1), item.price))
    } else if (salesCtx?.product) {
      pushSuggestion(
        salesCtx.product.title || 'Producto sugerido',
        1,
        salesCtx.product.price ? Number(salesCtx.product.price) : undefined,
        salesCtx.product.id
      )
    } else if (salesCtx?.lastMentionedProductId || salesCtx?.lastMentionedProductName) {
      pushSuggestion(
        salesCtx.lastMentionedProductName || 'Producto mencionado',
        1,
        undefined,
        salesCtx.lastMentionedProductId
      )
    }

    const urlProductName = searchParams.get('productName')
    if (urlProductName && !list.some((item) => item.name.toLowerCase() === urlProductName.toLowerCase())) {
      pushSuggestion(urlProductName, 1)
    }

    return list
  }, [conversationQuery.data, availableProducts, searchParams])

  useEffect(() => {
    const seedProductIds = searchParams.getAll('productId').filter(Boolean)
    const seedQuantities = searchParams.getAll('quantity')
    const seedSource = searchParams.get('source')
    const seedProductName = searchParams.get('productName')

    if (seedProductName) setProductSearch(seedProductName)

    setForm(
      createDefaultForm({
        source: isOrderSource(seedSource)
          ? seedSource
          : seedProductIds.length > 0 || seedProductName
            ? 'catalog'
            : seedConversationId
              ? 'checkout'
              : 'manual',
        conversationId: seedConversationId,
        customerName: searchParams.get('customerName') || '',
        customerPhone: searchParams.get('customerPhone') || '',
        fulfillmentMethod: searchParams.get('fulfillmentMethod') || 'delivery',
        fulfillmentAddress: searchParams.get('fulfillmentAddress') || '',
        items: seedProductIds.map((productId, index) => createEmptyOrderItem(productId, Number(seedQuantities[index] ?? 1))),
      })
    )
  }, [seedConversationId, searchParamsKey, searchParams])

  // El canal se resuelve cuando llegan las instancias, sin reiniciar el formulario ya escrito.
  useEffect(() => {
    const instances = channelInstancesQuery.data ?? []
    if (instances.length === 0) return

    setForm((current) => {
      const resolved = resolveOrderChannelInstanceId({
        currentChannelInstanceId: current.channelInstanceId,
        conversationChannelInstanceId: conversationQuery.data?.channelInstanceId || conversationQuery.data?.waInstanceId,
        conversationChannelType: conversationQuery.data?.channelType || searchParams.get('channelType'),
        urlChannelInstanceId: searchParams.get('channelInstanceId'),
        urlChannelType: searchParams.get('channelType'),
        instances,
        hasConversationContext: Boolean(seedConversationId),
      })

      return resolved && resolved !== current.channelInstanceId
        ? { ...current, channelInstanceId: resolved }
        : current
    })
  }, [channelInstancesQuery.data, conversationQuery.data, seedConversationId, searchParams])

  useEffect(() => {
    const handle = window.setTimeout(() => setContactSearchDebounced(contactSearch.trim()), 250)
    return () => window.clearTimeout(handle)
  }, [contactSearch])

  useEffect(() => {
    if (!conversationQuery.data) return
    const conv = conversationQuery.data
    setForm((current) => ({
      ...current,
      conversationId: conv.id,
      customerName: current.customerName || conv.contactName || '',
      customerPhone: current.customerPhone || conv.waContact || '',
      channelInstanceId: resolveOrderChannelInstanceId({
        currentChannelInstanceId: current.channelInstanceId,
        conversationChannelInstanceId: conv.channelInstanceId || conv.waInstanceId,
        conversationChannelType: conv.channelType,
        urlChannelInstanceId: searchParams.get('channelInstanceId'),
        urlChannelType: searchParams.get('channelType'),
        instances: channelInstancesQuery.data,
        hasConversationContext: true,
      }),
    }))
  }, [conversationQuery.data, channelInstancesQuery.data, searchParams])

  useEffect(() => {
    if (!selectedContact) return
    setForm((current) => ({
      ...current,
      contactId: selectedContact.id,
      customerName: current.customerName || selectedContact.name || '',
      customerPhone: current.customerPhone || selectedContact.phone || selectedContact.waContact || '',
      customerEmail: current.customerEmail || selectedContact.email || '',
    }))
  }, [selectedContact])

  useEffect(() => {
    if (!conversationQuery.data && !seedConversationId) return
    const conv = conversationQuery.data
    const salesCtx = conv?.salesContext
    const activeDraft = salesCtx?.activeOrderDraft
    const handoff = salesCtx?.handoff

    setForm((current) => {
      const hasAnyValidId = current.items.some((item) => item.productId.trim().length > 0)
      const autoMatched = !hasAnyValidId
        ? conversationSuggestions
            .filter((suggestion) => suggestion.matchedProduct?.id)
            .map((suggestion) => ({ productId: suggestion.matchedProduct!.id, quantity: Math.max(1, suggestion.quantity || 1) }))
        : []

      return {
        ...current,
        source: 'checkout',
        conversationId: conv?.id || seedConversationId || current.conversationId,
        channelInstanceId: resolveOrderChannelInstanceId({
          currentChannelInstanceId: current.channelInstanceId,
          conversationChannelInstanceId: conv?.channelInstanceId || conv?.waInstanceId,
          conversationChannelType: conv?.channelType,
          urlChannelInstanceId: searchParams.get('channelInstanceId'),
          urlChannelType: searchParams.get('channelType'),
          instances: channelInstancesQuery.data,
          hasConversationContext: true,
        }),
        customerName: current.customerName || handoff?.customerName || conv?.contactName || conv?.waContact || '',
        customerPhone: current.customerPhone || handoff?.customerPhone || conv?.waContact || '',
        fulfillmentMethod: handoff?.fulfillmentMethod || activeDraft?.fulfillmentMethod || current.fulfillmentMethod,
        fulfillmentAddress: handoff?.fulfillmentAddress || activeDraft?.fulfillmentAddress || current.fulfillmentAddress,
        fulfillmentReference: activeDraft?.fulfillmentReference || current.fulfillmentReference,
        notes: current.notes || (handoff || activeDraft ? 'Orden iniciada automáticamente desde conversación de WhatsApp.' : ''),
        items: autoMatched.length > 0 ? autoMatched : current.items,
      }
    })

    if (conversationSuggestions.length > 0 || activeDraft?.items?.length || handoff?.items?.length || salesCtx?.product) {
      setWizardStep(2)
    }
  }, [conversationQuery.data, availableProducts, conversationSuggestions, channelInstancesQuery.data, searchParams, seedConversationId])

  const availableContacts = useMemo(() => contactsQuery.data?.data ?? [], [contactsQuery.data])
  const filteredContacts = useMemo(() => {
    const term = contactSearchDebounced.trim().toLowerCase()
    if (!term) return availableContacts
    return availableContacts.filter((contact) =>
      [contact.name, contact.phone, contact.email, contact.waContact]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(term)
    )
  }, [availableContacts, contactSearchDebounced])

  const filteredProducts = useMemo(() => {
    const term = normalizeProductSearchText(productSearch)
    const base = availableProducts.slice()
    if (!term) return base.slice(0, 12)
    return base
      .filter((product) =>
        normalizeProductSearchText([product.name, product.sku, product.description].filter(Boolean).join(' ')).includes(term)
      )
      .slice(0, 12)
  }, [availableProducts, productSearch])

  const selectedProducts = useMemo(
    () =>
      form.items
        .map((item) => {
          const product = availableProducts.find((entry) => entry.id === item.productId)
          if (!product) return null
          return { ...product, quantity: item.quantity, lineTotalCents: product.priceCents * Math.max(1, item.quantity) }
        })
        .filter(Boolean) as Array<Product & { quantity: number; lineTotalCents: number }>,
    [availableProducts, form.items]
  )

  const totals = useMemo(
    () => ({ subtotal: selectedProducts.reduce((sum, product) => sum + product.lineTotalCents, 0), total: selectedProducts.reduce((sum, product) => sum + product.lineTotalCents, 0) }),
    [selectedProducts]
  )

  const pickupOptions = useMemo(() => {
    const zones = botSettingsQuery.data?.checkoutConfig?.shippingZones ?? []
    return zones.length > 0 ? zones : ['Sucursal principal', 'Sucursal centro', 'Retiro en depósito']
  }, [botSettingsQuery.data?.checkoutConfig?.shippingZones])

  const fulfillmentMode = form.fulfillmentMethod || 'delivery'
  const isDeliveryMode = isDeliveryLikeMethod(fulfillmentMode)
  const isPickupMode = isPickupLikeMethod(fulfillmentMode)
  const selectedChannelInstance = channelInstancesQuery.data?.find((instance) => instance.id === form.channelInstanceId) ?? null
  const fallbackChannelName = useMemo(() => {
    if (selectedChannelInstance) return `${selectedChannelInstance.instanceName} · ${selectedChannelInstance.channelType}`
    const conv = conversationQuery.data
    const targetType = conv?.channelType || searchParams.get('channelType')
    if (isWhatsAppChannelType(targetType)) {
      return conv?.waInstance?.instanceName ? `${conv.waInstance.instanceName} · WhatsApp` : 'WhatsApp'
    }
    return targetType || 'Sin canal seleccionado'
  }, [selectedChannelInstance, conversationQuery.data, searchParams])

  const wizardStepOneReady = Boolean(selectedContact || (form.customerName.trim() && form.customerPhone.trim()))
  const wizardStepTwoReady = form.items.length > 0 && form.items.every((item) => item.productId.trim())
  const wizardStepThreeReady =
    (!isDeliveryMode || Boolean(form.fulfillmentAddress.trim())) && (!isPickupMode || Boolean(form.fulfillmentReference.trim()))
  const seedProduct = availableProducts.find((product) =>
    form.items.some((item) => item.productId === product.id)
  )

  const createMutation = useMutation({
    mutationFn: (payload: Parameters<typeof ordersApi.createOrder>[0]) => ordersApi.createOrder(payload),
    onSuccess: async (order) => {
      toast.success('Orden creada')
      await queryClient.invalidateQueries({ queryKey: queryKeys.orders(tenantId) })
      await queryClient.invalidateQueries({ queryKey: queryKeys.ordersSummary(tenantId) })
      navigate(`/app/orders/${order.id}`, { replace: true })
    },
    onError: (error: any) => {
      const details = Array.isArray(error?.response?.data?.details) ? error.response.data.details : null
      if (details?.length) {
        toast.error(buildOrderValidationMessage(details))
        return
      }
      toast.error(error?.response?.data?.error || error?.message || 'No se pudo crear la orden')
    },
  })

  const updateOrderItem = (index: number, patch: Partial<OrderFormItem>) =>
    setForm((current) => ({ ...current, items: current.items.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item)) }))

  const addOrderItem = () => setForm((current) => ({ ...current, items: [...current.items, createEmptyOrderItem()] }))

  const removeOrderItem = (index: number) =>
    setForm((current) => {
      const nextItems = current.items.filter((_, itemIndex) => itemIndex !== index)
      return { ...current, items: nextItems.length > 0 ? nextItems : [createEmptyOrderItem()] }
    })

  const addProductToOrder = (product: Product) =>
    setForm((current) => {
      const existingIndex = current.items.findIndex((item) => item.productId === product.id)
      if (existingIndex >= 0) {
        return {
          ...current,
          items: current.items.map((item, index) => (index === existingIndex ? { ...item, quantity: Math.max(1, item.quantity + 1) } : item)),
        }
      }
      return { ...current, items: [...current.items.filter((item) => item.productId.trim()), { productId: product.id, quantity: 1 }] }
    })

  const selectExistingContact = (contact: Contact) => {
    setSelectedContact(contact)
    setForm((current) => ({
      ...current,
      contactId: contact.id,
      customerName: contact.name || current.customerName,
      customerPhone: contact.phone || contact.waContact || current.customerPhone,
      customerEmail: contact.email || current.customerEmail,
    }))
  }

  const createQuickContact = async () => {
    if (!quickContact.waContact.trim()) {
      toast.error('El cliente rápido necesita un WhatsApp o identificador de canal')
      return
    }
    try {
      const created = await contactsApi.createContact({
        waContact: quickContact.waContact.trim(),
        name: quickContact.name.trim() || undefined,
        phone: quickContact.phone.trim() || undefined,
        email: quickContact.email.trim() || undefined,
        source: 'manual',
      })
      setSelectedContact(created)
      setQuickContactOpen(false)
      setQuickContact({ waContact: '', name: '', phone: '', email: '' })
      toast.success('Cliente creado')
    } catch (error: any) {
      toast.error(error?.response?.data?.error || error?.message || 'No se pudo crear el cliente')
    }
  }

  const validateStep1 = () => {
    if (selectedContact || form.customerName.trim() || form.customerPhone.trim()) return true
    toast.error('Selecciona o crea un cliente antes de continuar')
    return false
  }

  const validateStep2 = () => {
    if (form.items.length === 0) {
      toast.error('Agrega al menos un producto')
      return false
    }
    if (form.items.some((item) => !item.productId.trim())) {
      toast.error('Completa todos los productos agregados')
      return false
    }
    return true
  }

  const submitCreate = () => {
    if (!validateStep1() || !validateStep2()) return
    if (isDeliveryMode && !form.fulfillmentAddress.trim()) {
      toast.error('La dirección es obligatoria para delivery')
      return
    }
    if (isPickupMode && !form.fulfillmentReference.trim()) {
      toast.error('Selecciona una sucursal para pickup')
      return
    }

    const items = form.items.map((item) => ({ productId: item.productId.trim(), quantity: Math.max(1, Number(item.quantity || 1)) }))
    if (items.length === 0) {
      toast.error('Selecciona al menos un producto')
      return
    }

    createMutation.mutate({
      source: form.source,
      conversationId: form.conversationId || undefined,
      contactId: form.contactId || undefined,
      channelInstanceId: form.channelInstanceId || conversationQuery.data?.channelInstanceId || undefined,
      customerName: form.customerName || undefined,
      customerPhone: form.customerPhone || undefined,
      customerEmail: form.customerEmail || undefined,
      fulfillmentMethod: form.fulfillmentMethod || undefined,
      fulfillmentAddress: form.fulfillmentAddress || undefined,
      fulfillmentReference: form.fulfillmentReference || undefined,
      notes: form.notes || undefined,
      items,
    })
  }

  const goToWizardStep = (nextStep: 1 | 2 | 3) => {
    if (nextStep === wizardStep) return
    if (nextStep === 2 && !validateStep1()) return
    if (nextStep === 3 && (!validateStep1() || !validateStep2())) return
    setWizardStep(nextStep)
  }

  const goToNextWizardStep = () => {
    if (wizardStep === 1) {
      if (!validateStep1()) return
      setWizardStep(2)
      return
    }
    if (wizardStep === 2) {
      if (!validateStep2()) return
      setWizardStep(3)
    }
  }

  const money = formatOrderMoney

  return (
    <Modal isOpen onClose={onClose} title="Crear orden" size="xl">
      <div className="space-y-4">
        <OrderWizardSummaryBar
          customerName={selectedContact?.name || form.customerName}
          customerPhone={selectedContact?.phone || form.customerPhone}
          itemCount={selectedProducts.length}
          totalLabel={money(totals.total)}
          fromConversation={Boolean(form.conversationId)}
          channelLabel={selectedChannelInstance?.instanceName ?? null}
        />
        <OrderWizardStepper steps={WIZARD_STEPS} current={wizardStep} onSelect={goToWizardStep} />

        {wizardStep === 1 ? (
          <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-2 text-slate-900">
              <User className="h-4 w-4 text-brand" />
              <h3 className="text-base font-semibold">Cliente y canal</h3>
            </div>

            <div className="rounded-xl border border-brand/15 bg-brand/5 p-4">
              <div className="flex items-center gap-2 text-brand">
                <ShoppingCart className="h-4 w-4" />
                <p className="text-sm font-semibold">Crear nueva orden</p>
              </div>
              <p className="mt-2 text-sm text-slate-600">Completa el producto y los datos mínimos. La orden quedará ligada al funnel actual.</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {seedConversationId ? (
                  <button type="button" onClick={() => navigate(`/app/conversations/${seedConversationId}`)} className="rounded-full border border-white/70 bg-white px-3 py-1.5 text-xs font-semibold text-brand shadow-sm transition-colors hover:bg-slate-50">
                    Abrir conversación
                  </button>
                ) : null}
                <button type="button" onClick={() => navigate('/app/conversations')} className="rounded-full border border-brand/15 bg-white px-3 py-1.5 text-xs font-semibold text-brand transition-colors hover:bg-slate-50">
                  Elegir conversación
                </button>
                <button type="button" onClick={() => navigate('/app/products')} className="rounded-full border border-brand/15 bg-white px-3 py-1.5 text-xs font-semibold text-brand transition-colors hover:bg-slate-50">
                  Ver productos
                </button>
              </div>
              {(seedConversationId && conversationQuery.data) || seedProduct ? (
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  {seedConversationId && conversationQuery.data ? (
                    <div className="rounded-lg bg-white p-3 text-sm shadow-sm ring-1 ring-slate-100">
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Conversación origen</p>
                      <p className="mt-1 font-semibold text-slate-900">{conversationQuery.data.contactName || conversationQuery.data.waContact}</p>
                      <p className="text-xs text-slate-500">{conversationQuery.data.waContact}</p>
                    </div>
                  ) : null}
                  {seedProduct ? (
                    <div className="rounded-lg bg-white p-3 text-sm shadow-sm ring-1 ring-slate-100">
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Producto precargado</p>
                      <p className="mt-1 font-semibold text-slate-900">{seedProduct.name}</p>
                      <p className="text-xs text-slate-500">{seedProduct.sku} · {money(seedProduct.priceCents, seedProduct.currency)}</p>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>

            {conversationSuggestions.length > 0 ? (
              <div className="flex items-center gap-2.5 rounded-xl border border-emerald-200 bg-emerald-50/70 p-3 text-xs text-emerald-900 shadow-sm">
                <Bot className="h-4 w-4 shrink-0 text-emerald-700" />
                <div className="min-w-0">
                  <p className="font-bold">Pedido sugerido desde conversación de WhatsApp</p>
                  <p className="truncate text-slate-600">
                    {conversationSuggestions.map((suggestion) => `${suggestion.quantity}x ${suggestion.matchedProduct ? suggestion.matchedProduct.name : suggestion.name}`).join(', ')}
                  </p>
                </div>
              </div>
            ) : null}

            <div className="grid gap-4 lg:grid-cols-[1.4fr_0.9fr]">
              <div className="space-y-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="order-contact-search">Buscar cliente</label>
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <Input id="order-contact-search" className="pl-9" value={contactSearch} onChange={(event) => setContactSearch(event.target.value)} placeholder="Nombre, teléfono, email o WhatsApp" />
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">Clientes sugeridos</p>
                      <p className="text-xs text-slate-500">Selecciona uno para completar los datos al instante.</p>
                    </div>
                    <Button type="button" variant="outline" onClick={() => setQuickContactOpen((current) => !current)}>
                      <Plus className="mr-2 h-4 w-4" />
                      {quickContactOpen ? 'Cerrar cliente rápido' : 'Crear cliente rápido'}
                    </Button>
                  </div>

                  {quickContactOpen ? (
                    <div className="mt-3 grid gap-3 rounded-xl border border-dashed border-brand/20 bg-white p-3">
                      <div className="grid gap-3 sm:grid-cols-2">
                        <Input value={quickContact.waContact} onChange={(event) => setQuickContact((current) => ({ ...current, waContact: event.target.value }))} placeholder="WhatsApp o canal" aria-label="WhatsApp o canal del cliente" />
                        <Input value={quickContact.name} onChange={(event) => setQuickContact((current) => ({ ...current, name: event.target.value }))} placeholder="Nombre del cliente" aria-label="Nombre del cliente" />
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <Input value={quickContact.phone} onChange={(event) => setQuickContact((current) => ({ ...current, phone: event.target.value }))} placeholder="Teléfono" aria-label="Teléfono del cliente" />
                        <Input type="email" value={quickContact.email} onChange={(event) => setQuickContact((current) => ({ ...current, email: event.target.value }))} placeholder="Correo" aria-label="Correo del cliente" />
                      </div>
                      <div className="flex justify-end">
                        <Button type="button" className="bg-brand text-white hover:bg-brand-dark" onClick={createQuickContact}>
                          <User className="mr-2 h-4 w-4" />
                          Guardar cliente
                        </Button>
                      </div>
                    </div>
                  ) : null}

                  <div className="mt-3 max-h-48 space-y-2 overflow-y-auto pr-1">
                    {contactsQuery.isLoading ? (
                      <div className="rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-500">Buscando clientes...</div>
                    ) : filteredContacts.length > 0 ? (
                      filteredContacts.map((contact) => {
                        const isSelected = selectedContact?.id === contact.id
                        return (
                          <button
                            key={contact.id}
                            type="button"
                            onClick={() => selectExistingContact(contact)}
                            className={[
                              'w-full rounded-xl border px-3 py-3 text-left transition-colors',
                              isSelected ? 'border-brand/30 bg-brand/5' : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50',
                            ].join(' ')}
                          >
                            <div className="flex items-center justify-between gap-3">
                              <div className="min-w-0">
                                <p className="truncate text-sm font-semibold text-slate-900">{contact.name || contact.waContact}</p>
                                <p className="truncate text-xs text-slate-500">
                                  {contact.phone || contact.waContact} {contact.email ? `· ${contact.email}` : ''}
                                </p>
                              </div>
                              {isSelected ? <span className="rounded-full bg-emerald-100 px-2 py-1 text-[10px] font-semibold text-emerald-700">Seleccionado</span> : null}
                            </div>
                          </button>
                        )
                      })
                    ) : (
                      <div className="rounded-lg border border-dashed border-slate-200 bg-white p-3 text-sm text-slate-500">No hay coincidencias. Puedes crear un cliente rápido.</div>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                    <User className="h-4 w-4" />
                    Cliente seleccionado
                  </div>
                  {selectedContact ? (
                    <div className="mt-3 space-y-2">
                      <p className="text-base font-semibold text-slate-900">{selectedContact.name || 'Sin nombre'}</p>
                      <p className="text-sm text-slate-600">{selectedContact.phone || selectedContact.waContact}</p>
                      {selectedContact.email ? <p className="text-sm text-slate-600">{selectedContact.email}</p> : null}
                      <p className="text-xs text-slate-500">{selectedContact.source} · {selectedContact.totalConversations} conversaciones</p>
                    </div>
                  ) : (
                    <p className="mt-3 text-sm text-slate-500">Aún no seleccionaste un cliente.</p>
                  )}
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                    <Clock3 className="h-4 w-4" />
                    Órdenes recientes
                  </div>
                  <div className="mt-3 space-y-2">
                    {selectedContact ? (
                      recentOrdersQuery.isLoading ? (
                        <p className="text-sm text-slate-500">Cargando historial...</p>
                      ) : (recentOrdersQuery.data?.orders ?? []).length > 0 ? (
                        (recentOrdersQuery.data?.orders ?? []).slice(0, 3).map((order) => (
                          <div key={order.id} className="rounded-lg border border-white bg-white px-3 py-2 shadow-sm">
                            <div className="flex items-center justify-between gap-2">
                              <p className="text-sm font-semibold text-slate-900">{order.orderNumber}</p>
                              <span className="text-xs text-slate-500">{getOrderStatusBadge(order.status)?.label}</span>
                            </div>
                            <p className="mt-1 text-xs text-slate-500">{new Date(order.createdAt).toLocaleString('es-VE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })} · {money(order.totalCents, order.currency)}</p>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-slate-500">No hay órdenes recientes para este cliente.</p>
                      )
                    ) : (
                      <p className="text-sm text-slate-500">Selecciona un cliente para ver su historial.</p>
                    )}
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                    <Package className="h-4 w-4" />
                    Canal y origen
                  </div>
                  <div className="mt-3 grid gap-3">
                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500" htmlFor="order-source">Origen comercial</label>
                      <select id="order-source" className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm" value={form.source} onChange={(event) => setForm((current) => ({ ...current, source: event.target.value as OrderSource }))}>
                        <option value="manual">Manual</option>
                        <option value="catalog">Catálogo</option>
                        <option value="checkout">Checkout</option>
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500" htmlFor="order-channel">Canal</label>
                      <select id="order-channel" className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm" value={form.channelInstanceId} onChange={(event) => setForm((current) => ({ ...current, channelInstanceId: event.target.value }))}>
                        <option value="">Selecciona un canal</option>
                        {(channelInstancesQuery.data ?? []).map((instance: ChannelInstanceResponse) => (
                          <option key={instance.id} value={instance.id}>{instance.instanceName} · {instance.channelType}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 -mx-5 -mb-5 flex justify-end gap-2 rounded-b-2xl border-t border-slate-200 bg-white/95 px-5 py-3 backdrop-blur">
              <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
              <Button type="button" className="bg-brand text-white hover:bg-brand-dark" onClick={goToNextWizardStep} disabled={!wizardStepOneReady}>Siguiente</Button>
            </div>
          </div>
        ) : null}

        {wizardStep === 2 ? (
          <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-2 text-slate-900">
              <Package className="h-4 w-4 text-brand" />
              <h3 className="text-base font-semibold">Productos</h3>
            </div>

            {conversationSuggestions.length > 0 ? (
              <div className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 via-teal-50/40 to-white p-4 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-emerald-100 pb-2.5">
                  <div className="flex items-center gap-2">
                    <Bot className="h-4 w-4 text-emerald-700" />
                    <span className="text-xs font-bold uppercase tracking-wider text-emerald-950">Productos detectados en la conversación</span>
                  </div>
                  <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-800">
                    WhatsApp: {conversationQuery.data?.contactName || conversationQuery.data?.waContact || 'Cliente'}
                  </span>
                </div>

                <div className="mt-3 grid gap-2.5 sm:grid-cols-2">
                  {conversationSuggestions.map((suggestion, index) => {
                    const isAlreadyAdded = form.items.some((item) => item.productId === suggestion.matchedProduct?.id)
                    return (
                      <div key={`${suggestion.matchedProduct?.id ?? suggestion.name}-${index}`} className="flex items-center justify-between gap-3 rounded-xl border border-emerald-200/90 bg-white p-2.5 text-xs shadow-sm">
                        <div className="flex min-w-0 items-center gap-2.5">
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200">
                            <Package className="h-4 w-4" />
                          </div>
                          <div className="min-w-0">
                            <p className="truncate font-bold text-slate-900">{suggestion.quantity}x {suggestion.matchedProduct ? suggestion.matchedProduct.name : suggestion.name}</p>
                            <p className="truncate text-[11px] text-slate-500">
                              {suggestion.price != null ? `$${suggestion.price.toFixed(2)}` : 'Según inventario'}
                              {suggestion.matchedProduct ? ` · SKU ${suggestion.matchedProduct.sku}` : ' · Sin coincidencia directa'}
                            </p>
                          </div>
                        </div>

                        <div className="shrink-0">
                          {suggestion.matchedProduct ? (
                            isAlreadyAdded ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-bold text-emerald-800">
                                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-700" /> En orden
                              </span>
                            ) : (
                              <Button type="button" size="sm" className="h-7 rounded-full bg-brand px-3 text-[11px] font-bold text-white hover:bg-brand-dark" onClick={() => addProductToOrder(suggestion.matchedProduct!)}>
                                <Plus className="mr-1 h-3.5 w-3.5" /> Agregar
                              </Button>
                            )
                          ) : (
                            <Button type="button" size="sm" variant="outline" className="h-7 rounded-full border-amber-300 bg-amber-50 px-2.5 text-[11px] font-bold text-amber-800 hover:bg-amber-100" onClick={() => setProductSearch(suggestion.name)} title="Buscar este producto en el catálogo">
                              <Search className="mr-1 h-3.5 w-3.5 text-amber-600" /> Buscar
                            </Button>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            ) : null}

            <div className="grid gap-4 lg:grid-cols-[1fr_0.9fr]">
              <div className="space-y-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="order-product-search">Buscar producto</label>
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <Input id="order-product-search" className="pl-9" value={productSearch} onChange={(event) => setProductSearch(event.target.value)} placeholder="Nombre, SKU o descripción" />
                  </div>
                </div>

                <div className="grid gap-3">
                  {filteredProducts.length > 0 ? (
                    filteredProducts.map((product) => {
                      const isSuggested = conversationSuggestions.some((suggestion) => suggestion.matchedProduct?.id === product.id)
                      return (
                        <button key={product.id} type="button" onClick={() => addProductToOrder(product)} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-3 text-left transition-colors hover:border-brand/20 hover:bg-brand/5">
                          <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white ring-1 ring-slate-200">
                            {product.imageUrl ? <img src={product.imageUrl} alt="" className="h-full w-full object-cover" /> : <Package className="h-5 w-5 text-slate-400" />}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                              <p className="truncate text-sm font-semibold text-slate-900">{product.name}</p>
                              {isSuggested ? <span className="rounded-full bg-emerald-100 px-1.5 text-[10px] font-bold text-emerald-800">Sugerido</span> : null}
                            </div>
                            <p className="truncate text-xs text-slate-500">SKU {product.sku} · {money(product.priceCents, product.currency)}</p>
                          </div>
                          <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-brand shadow-sm">Agregar</span>
                        </button>
                      )
                    })
                  ) : (
                    <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">No hay resultados. Prueba otro nombre o SKU.</div>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">Productos agregados</p>
                      <p className="text-xs text-slate-500">Cada línea guarda snapshot de precio y cantidad.</p>
                    </div>
                    <Button type="button" variant="outline" onClick={addOrderItem}><Plus className="mr-2 h-4 w-4" />Agregar otro producto</Button>
                  </div>

                  <div className="mt-3 space-y-3">
                    {form.items.map((item, index) => {
                      const selectedProduct = availableProducts.find((product) => product.id === item.productId) ?? null
                      return (
                        <div key={`${item.productId || 'empty'}-${index}`} className="rounded-2xl border border-white bg-white p-4 shadow-sm ring-1 ring-slate-100">
                          {selectedProduct ? (
                            <div className="space-y-3">
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="truncate text-sm font-semibold text-slate-900">{selectedProduct.name}</p>
                                  <p className="text-xs text-slate-500">SKU {selectedProduct.sku} · {money(selectedProduct.priceCents, selectedProduct.currency)}</p>
                                </div>
                                <Button type="button" variant="outline" onClick={() => removeOrderItem(index)} disabled={form.items.length === 1} className="shrink-0"><Trash2 className="mr-2 h-4 w-4" />Eliminar</Button>
                              </div>
                              <div className="grid gap-3 sm:grid-cols-[120px_1fr]">
                                <div>
                                  <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500" htmlFor={`order-item-quantity-${index}`}>Cantidad</label>
                                  <Input id={`order-item-quantity-${index}`} type="number" min={1} value={item.quantity} onChange={(event) => updateOrderItem(index, { quantity: Math.max(1, Number(event.target.value || 1)) })} />
                                </div>
                                <div className="rounded-xl bg-slate-50 px-3 py-3 text-xs text-slate-600">
                                  Subtotal de línea: <span className="font-semibold text-slate-900">{money(selectedProduct.priceCents * Math.max(1, Number(item.quantity || 1)), selectedProduct.currency)}</span>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              <div className="flex items-center justify-between gap-3">
                                <div>
                                  <p className="text-sm font-semibold text-slate-900">Producto {index + 1}</p>
                                  <p className="text-xs text-slate-500">Selecciona una línea para completar la orden.</p>
                                </div>
                                <Button type="button" variant="outline" onClick={() => removeOrderItem(index)} disabled={form.items.length === 1}><Trash2 className="mr-2 h-4 w-4" />Eliminar</Button>
                              </div>
                              {conversationSuggestions.find((suggestion) => !suggestion.matchedProduct) ? (
                                <div className="flex items-center justify-between gap-2 rounded-xl border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
                                  <span className="truncate">
                                    El cliente solicitó en WhatsApp: <strong>{conversationSuggestions.find((suggestion) => !suggestion.matchedProduct)?.name}</strong>
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => setProductSearch(conversationSuggestions.find((suggestion) => !suggestion.matchedProduct)!.name)}
                                    className="shrink-0 rounded border border-amber-300 bg-white px-2 py-0.5 font-bold text-amber-800 shadow-sm hover:bg-amber-100"
                                  >
                                    Buscar
                                  </button>
                                </div>
                              ) : null}
                              <select className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm" value={item.productId} onChange={(event) => updateOrderItem(index, { productId: event.target.value })} aria-label={`Producto de la línea ${index + 1}`}>
                                <option value="">Selecciona un producto</option>
                                {availableProducts.map((product) => <option key={product.id} value={product.id}>{product.name} · {product.sku} · {money(product.priceCents, product.currency)}</option>)}
                              </select>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>

                  <div className="mt-4 rounded-2xl bg-white px-4 py-3 shadow-sm ring-1 ring-slate-100">
                    <div className="flex items-center justify-between gap-3"><span className="text-sm font-medium text-slate-600">Subtotal</span><span className="text-base font-semibold text-slate-900">{money(totals.subtotal)}</span></div>
                    <div className="mt-2 flex items-center justify-between gap-3"><span className="text-sm font-medium text-slate-600">Total</span><span className="text-lg font-bold text-slate-900">{money(totals.total)}</span></div>
                  </div>
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 -mx-5 -mb-5 flex items-center justify-between gap-2 rounded-b-2xl border-t border-slate-200 bg-white/95 px-5 py-3 backdrop-blur">
              <Button type="button" variant="outline" onClick={() => setWizardStep(1)}>Atrás</Button>
              <Button type="button" className="bg-brand text-white hover:bg-brand-dark" onClick={goToNextWizardStep} disabled={!wizardStepTwoReady}>
                Siguiente{selectedProducts.length > 0 ? ` · ${money(totals.total)}` : ''}
              </Button>
            </div>
          </div>
        ) : null}

        {wizardStep === 3 ? (
          <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-2 text-slate-900">
              <ShoppingCart className="h-4 w-4 text-brand" />
              <h3 className="text-base font-semibold">Detalles y confirmación</h3>
            </div>

            <div className="grid gap-4 lg:grid-cols-[1fr_0.9fr]">
              <div className="space-y-4">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-sm font-semibold text-slate-900">Tipo de entrega</p>
                  <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-5">
                    {[
                      { value: 'delivery', label: 'Delivery', icon: Truck },
                      { value: 'pickup', label: 'Pickup', icon: Home },
                      { value: 'cash_on_delivery', label: 'Cobro destino', icon: ShoppingCart },
                      { value: 'shipping', label: 'Envío nacional', icon: Package },
                      { value: 'store_pickup', label: 'Retiro en tienda', icon: Home },
                    ].map((option) => {
                      const isActive = fulfillmentMode === option.value
                      const Icon = option.icon
                      return (
                        <button key={option.value} type="button" aria-pressed={isActive} onClick={() => setForm((current) => ({ ...current, fulfillmentMethod: option.value }))} className={['rounded-xl border px-3 py-3 text-left transition-colors', isActive ? 'border-brand/30 bg-brand/5 text-brand' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'].join(' ')}>
                          <Icon className="h-4 w-4" />
                          <p className="mt-2 text-sm font-semibold">{option.label}</p>
                        </button>
                      )
                    })}
                  </div>
                </div>

                {isDeliveryMode ? (
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-sm font-semibold text-slate-900">Dirección de entrega</p>
                    <div className="mt-3 space-y-3">
                      <Input value={form.fulfillmentAddress} onChange={(event) => setForm((current) => ({ ...current, fulfillmentAddress: event.target.value }))} placeholder="Dirección completa" aria-label="Dirección de entrega" />
                      <Input value={form.fulfillmentReference} onChange={(event) => setForm((current) => ({ ...current, fulfillmentReference: event.target.value }))} placeholder="Referencia adicional" aria-label="Referencia de entrega" />
                    </div>
                  </div>
                ) : null}

                {isPickupMode ? (
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-sm font-semibold text-slate-900">Sucursales disponibles</p>
                    <div className="mt-3 grid gap-2 sm:grid-cols-2">
                      {pickupOptions.map((branch) => (
                        <button type="button" key={branch} aria-pressed={form.fulfillmentReference === branch} onClick={() => setForm((current) => ({ ...current, fulfillmentReference: branch }))} className={['rounded-xl border px-3 py-3 text-left text-sm transition-colors', form.fulfillmentReference === branch ? 'border-brand/30 bg-brand/5 text-brand' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'].join(' ')}>{branch}</button>
                      ))}
                    </div>
                  </div>
                ) : null}

                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-center justify-between gap-3"><p className="text-sm font-semibold text-slate-900">Observaciones</p><span className="text-xs text-slate-500">{form.notes.length} caracteres</span></div>
                  <textarea value={form.notes} onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))} rows={4} maxLength={500} aria-label="Observaciones de la orden" className="mt-3 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none transition-colors focus:border-brand focus:ring-1 focus:ring-brand/20" placeholder="Observaciones de la orden" />
                  <div className="mt-3 flex flex-wrap gap-2">
                    {ORDER_NOTE_TEMPLATES.map((template) => (
                      <button key={template} type="button" onClick={() => setForm((current) => ({ ...current, notes: current.notes ? `${current.notes}\n${template}` : template }))} className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50">{template}</button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-sm font-semibold text-slate-900">Resumen final</p>
                  <ul className="mt-3 divide-y divide-slate-200 rounded-xl bg-white px-3 ring-1 ring-slate-100">
                    {selectedProducts.map((product) => (
                      <li key={product.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                        <span className="min-w-0 truncate text-slate-700"><span className="font-bold text-slate-900">{product.quantity}×</span> {product.name}</span>
                        <span className="shrink-0 font-semibold text-slate-900">{money(product.lineTotalCents, product.currency)}</span>
                      </li>
                    ))}
                    <li className="flex items-center justify-between py-2.5 text-sm">
                      <span className="font-semibold text-slate-600">Total</span>
                      <span className="text-lg font-black text-emerald-900">{money(totals.total)}</span>
                    </li>
                  </ul>
                  <div className="mt-3 space-y-3 text-sm text-slate-600">
                    <div>
                      <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Cliente</p>
                      <p className="mt-1 font-semibold text-slate-900">{selectedContact?.name || form.customerName || 'Sin cliente'}</p>
                      <p className="text-xs text-slate-500">{selectedContact?.phone || form.customerPhone || 'Sin teléfono'}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Canal</p>
                      <p className="mt-1 font-semibold text-slate-900">
                        {selectedChannelInstance ? `${selectedChannelInstance.instanceName} · ${selectedChannelInstance.channelType}` : fallbackChannelName}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Entrega</p>
                      <p className="mt-1 font-semibold text-slate-900">{getFulfillmentModeLabel(fulfillmentMode)}</p>
                      <p className={['text-xs', (isDeliveryMode ? form.fulfillmentAddress : form.fulfillmentReference) ? 'text-slate-500' : 'font-semibold text-amber-600'].join(' ')}>
                        {isDeliveryMode ? form.fulfillmentAddress || 'Falta dirección' : form.fulfillmentReference || 'Falta sucursal'}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="rounded-2xl border border-brand/15 bg-brand/5 p-4">
                  <div className="flex items-center gap-2 text-brand"><CheckCircle2 className="h-4 w-4" /><p className="text-sm font-semibold">Todo listo para crear</p></div>
                  <p className="mt-2 text-sm text-slate-600">Revisa el resumen y crea la orden para dejar el registro listo en el sistema y el funnel.</p>
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 -mx-5 -mb-5 flex items-center justify-between gap-3 rounded-b-2xl border-t border-slate-200 bg-white/95 px-5 py-3 backdrop-blur">
              <Button type="button" variant="outline" onClick={() => setWizardStep(2)}>Atrás</Button>
              <Button type="button" className="bg-brand px-5 py-3 text-white hover:bg-brand-dark" onClick={submitCreate} disabled={createMutation.isPending || !wizardStepThreeReady}>
                {createMutation.isPending ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Creando...</>) : (<><ShoppingCart className="mr-2 h-4 w-4" />Crear orden · {money(totals.total)}</>)}
              </Button>
            </div>
          </div>
        ) : null}

        <p className="text-center text-xs text-slate-500">
          {profile.terminology.primaryTransactionSingular} · los datos quedan ligados al negocio actual.
        </p>
      </div>
    </Modal>
  )
}
